import { BaseLogicExpression } from "./BaseLogic";
import { ExpressionValue } from "../../ExpressionValue";
import { AutoBind } from "../../../Common/AutoBind";
import { AndGroup } from "./AndGroup";
import { DataUtilities } from "../../../Common/DataUtilities";
import { StateManager } from "../../../Common/StateManager";
import { ExecutionData } from "../../../Common/Data/ExecutionData/ExecutionData";
import { NumberDataDefinition } from "../../../Common/Data/Definitions/NumberDataDefinition";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { BooleanDataDefinition } from "../../../Common/Data/Definitions/BooleanDataDefinition";
import { IConfigStorage } from "../../../Common/IConfigStorage";
import { ExpressionValueType } from "../../ExpressionValueType";
import { ITestingContext } from "../../../Common/Testing/ITestingContext";
import { IBaseExpressionElement } from "../../IBaseExpressionElement";
import { ExpressionContext } from "../../ExpressionContext";
import { ExpressionConstants } from "../../ExpressionContants";
import { ExpressionTestResult, ExpressionValueTestResult } from "../../ExpressionTestResult";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

export enum IfThenStyle {
    DATA_RETURN = "data_return",
    BRANCH_DATA_RETURN = "branch_data_return",
    LOGICAL_RETURN = "logical_return",
}

export class IfThenPair implements IBaseExpressionElement, IConfigStorage {
    readonly __type: string = "If Then Pair";
    readonly canSelect: boolean = false;

    ifExpression: AndGroup = new AndGroup();
    thenExpression: ExpressionValue = new ExpressionValue();
    _id: string = DataUtilities.generateUniqueId();

    expressionContext?: ExpressionContext;

    setExpressionContext(expressionContext1: ExpressionContext, owner: IBaseExpressionElement): void {
        this.expressionContext = expressionContext1;

        expressionContext1.register(this, owner);

        if (expressionContext1) {
            this.ifExpression.setExpressionContext(expressionContext1, this);
            this.thenExpression.setExpressionContext(expressionContext1, this);
        }
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "ifExpression", this.ifExpression.getStorageData());
        Reflect.set(data, "thenExpression", this.thenExpression.getStorageData());

        return data;
    }

    setStorageData(data: object): void {
        this._id = Reflect.get(data, "_id");
        this.ifExpression.setStorageData(Reflect.get(data, "ifExpression"));
        this.thenExpression.setStorageData(Reflect.get(data, "thenExpression"));
    }
}

// todo.  this is one where we can change output
export class IfThenExpression extends BaseLogicExpression {
    readonly __type: string = ExpressionConstants.IfThenExpressionType;

    // serialized by base class
    trueOutput: ExpressionValue = new ExpressionValue();
    // serialized by base class
    defaultOutput: ExpressionValue = new ExpressionValue();
    showElseAsDefault: boolean = false;
    showSimpleReturn: boolean = true;
    thenText: string = "result is";
    defaultText: string = "default result is";

    constructor();

    constructor(outputType: IDataDefinition = new NumberDataDefinition()) {
        super();

        if (outputType) this.expressionValueType = outputType;

        if (this._ifBranches.length === 0) {
            this.addIfBranch();
        }

        // set name and register expression values
        this.trueOutput = ExpressionValue.createExpressionValue(this, "trueOutput", this.expressionValueType);
        this.defaultOutput = ExpressionValue.createExpressionValue(this, "defaultOutput", this.expressionValueType);
    }

    _ifBranches: IfThenPair[] = [];

    get ifBranches(): IfThenPair[] {
        return this._ifBranches;
    }

    private _ifThenStyle: IfThenStyle = IfThenStyle.DATA_RETURN;

    get ifThenStyle(): IfThenStyle {
        return this._ifThenStyle;
    }

    set ifThenStyle(value: IfThenStyle) {
        if (value === IfThenStyle.LOGICAL_RETURN) {
            this.expressionValueType = new BooleanDataDefinition();
            this.trueOutput.expectedType = new BooleanDataDefinition();
            this.trueOutput.valueType = ExpressionValueType.VALUE;
            this.trueOutput.value = true;
            this.defaultOutput.valueType = ExpressionValueType.VALUE;
            this.defaultOutput.value = false;
            this.defaultOutput.expectedType = new BooleanDataDefinition();
        }
        this._ifThenStyle = value;

        this.onUpdate();
    }

    get expressionValueType() {
        return this._expressionValueType;
    }

    set expressionValueType(outputType: IDataDefinition) {
        if (this._ifThenStyle === IfThenStyle.LOGICAL_RETURN) {
            outputType = new BooleanDataDefinition();
        }
        this._expressionValueType = outputType;

        for (let ifBranch of this._ifBranches) {
            ifBranch.thenExpression.expectedType = outputType;
        }

        this.trueOutput.expectedType = outputType;
        this.defaultOutput.expectedType = outputType;
    }

    getExpressionValues(): ExpressionValue[] {
        let values: ExpressionValue[] = [];

        this._ifBranches.forEach((value: IfThenPair) => {
            values.push(...value.ifExpression.getExpressionValues());

            if (this._ifThenStyle === IfThenStyle.BRANCH_DATA_RETURN) {
                values.push(value.thenExpression);
            }
        });

        if (this._ifThenStyle !== IfThenStyle.LOGICAL_RETURN) {
            if (this._ifThenStyle === IfThenStyle.DATA_RETURN) {
                values.push(this.trueOutput);
            }
            values.push(this.defaultOutput);
        }

        return values;
    }

    getAllVariables(): string[] {
        let output: string[] = [];

        for (let f of this.getExpressionValues()) {
            output.push(...f.getAllVariables());
        }

        return output;
    }

    addIfBranch(): IfThenPair;

    addIfBranch(pair?: IfThenPair): IfThenPair {
        if (!pair) pair = new IfThenPair();

        pair.thenExpression.expectedType = this.expressionValueType;

        this._ifBranches.push(pair);

        StateManager.changed(this);

        if (this.expressionContext) {
            pair.setExpressionContext(this.expressionContext, this);

            this.onUpdate();
        }

        return pair;
    }

    deleteIfBranch(index: number) {
        if (this._ifBranches.length <= 1) {
            const replacement = new IfThenPair();
            replacement.thenExpression.expectedType = this.expressionValueType;
            this._ifBranches = [replacement];

            if (this.expressionContext) {
                replacement.setExpressionContext(this.expressionContext, this);
            }
        } else {
            this._ifBranches.splice(index, 1);
        }

        this.onUpdate();
        StateManager.changed(this);
    }

    setExpressionContext(expressionContext: ExpressionContext, owner: IBaseExpressionElement): void {
        super.setExpressionContext(expressionContext, owner);

        for (let p of this._ifBranches) {
            p.setExpressionContext(expressionContext, this);
        }
    }

    evaluate(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        for (let ifThenPair of this.ifBranches) {
            let value = ifThenPair.ifExpression.evaluate(data, testContext);

            if (testContext) {
                testContext.addTestResultItem(new ExpressionValueTestResult(ifThenPair._id, value.toString()));
            }

            if (value) {
                if (this.ifThenStyle === IfThenStyle.BRANCH_DATA_RETURN) {
                    return ifThenPair.thenExpression.evaluate(data, testContext);
                } else if (this.ifThenStyle === IfThenStyle.DATA_RETURN) {
                    value = this.trueOutput.evaluate(data, testContext);

                    if (testContext) {
                        testContext.addTestResultItem(
                            new ExpressionValueTestResult(this.trueOutput._id, value?.toString() || "undefined"),
                        );
                    }
                    return value;
                } else {
                    if (testContext) {
                        testContext.addTestResultItem(new ExpressionValueTestResult(this._id, "true"));
                    }

                    return true;
                }
            }
        }

        if (this.ifThenStyle === IfThenStyle.BRANCH_DATA_RETURN || this.ifThenStyle === IfThenStyle.DATA_RETURN) {
            let value = this.defaultOutput.evaluate(data, testContext);

            if (testContext) {
                testContext.addTestResultItem(
                    new ExpressionTestResult(
                        this.defaultOutput._id,
                        value.toString(),
                        this.expressionContext?.expression!,
                    ),
                );
            }

            return value;
        } else {
            if (testContext) {
                testContext.addTestResultItem(new ExpressionValueTestResult(this._id, "false"));
            }
            return false;
        }
    }

    getStorageData(): object {
        let data = super.getStorageData();

        let branches: object[] = [];

        for (let e of this._ifBranches) {
            branches.push(e.getStorageData());
        }

        Reflect.set(data, "branches", branches);

        Reflect.set(data, "showElseAsDefault", this.showElseAsDefault);
        Reflect.set(data, "showSimpleReturn", this.showSimpleReturn);
        Reflect.set(data, "ifThenStyle", this._ifThenStyle);
        Reflect.set(data, "thenText", this.thenText);
        Reflect.set(data, "defaultText", this.defaultText);

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        let branchData: object[] = Reflect.get(data, "branches") as object[];
        this._ifBranches = [];

        for (let e of branchData) {
            let exp = new IfThenPair();
            exp.setStorageData(e);
            this._ifBranches.push(exp);
        }

        this.showElseAsDefault = Reflect.get(data, "showElseAsDefault");
        this.showSimpleReturn = Reflect.get(data, "showSimpleReturn");
        this._ifThenStyle = Reflect.get(data, "ifThenStyle");
        this.thenText = Reflect.get(data, "thenText");
        this.defaultText = Reflect.get(data, "defaultText");
    }

    @AutoBind
    private onUpdate() {
        this.expressionContext?.refresh();
    }
}
